import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { Event } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useDispatch, useSelector } from 'react-redux'
import { PrivateLoader } from 'src/components/templates'
import { Options } from 'src/components/UI/CustomUI/atoms/FieldsFilter/types'
import {
    companiesActions,
    ordersActions,
    siteVisitsActions,
    territoriesActions,
    territoryActions,
    vendorsActions
} from 'src/ducks/actions'
import { getCompanies } from 'src/ducks/companies/selectors'
import { OrderType } from 'src/ducks/orders/types'
import { getEmployeePrimaryEmail, getOrders, getSiteVisits, getTerritories, getVendors } from 'src/ducks/selectors'
import { EMPLOYEE_ROLE_TYPE, formatTimestampDate, ORDER_STATUS } from 'src/helpers'
import { Box, Divider } from '../../../UI'
import { preAffiliateOptions, preTerritoryOptions } from './constants'
import { CalendarContext } from './context.provider'
import { getTerritoryParam, rainbow } from './helper'
import CalendarSidebar from './SideBar'
import CalendarToolbar from './ToolBar'
import { VendorOptionProp, ViewType } from './types'
import ViewCalendar from './ViewCalendar'
import ViewEvent from './ViewEvent'
import ViewScheduler from './ViewScheduler'

const defaultSelected = moment()

export const getOrderStatusColor = (status: string) => {
  switch (status) {
    case ORDER_STATUS.COMPLETE:
    case ORDER_STATUS.APPROVED:
      return '#17B975'
    case ORDER_STATUS.PRO_REJECTED:
    case ORDER_STATUS.REJECTED:
    case ORDER_STATUS.CANCELLED:
      return '#E01F1F'
    case ORDER_STATUS.DISPATCHED:
      return '#3A66FF'
    case ORDER_STATUS.REVIEW:
      return '#0071A3'
    case ORDER_STATUS.PENDING:
      return '#E0007D'
    case ORDER_STATUS.IN_PROGRESS:
    default:
      return '#00A9B8'
  }
}

const CalendarPage: FC = () => {
  const dispatch = useDispatch()

  const [selected, setSelected] = useState<moment.Moment | null>(
    defaultSelected
  )

  const orders = useSelector(getOrders())
  const siteVisits = useSelector(getSiteVisits())
  const territories = useSelector(getTerritories)
  const affiliatesStages = useSelector(getCompanies)
  const vendors = useSelector(getVendors())

  const [view, setView] = useState<ViewType>('month')
  const [showDispatch, setShowDispatch] = useState<boolean>(false)
  const [ordersEvents, setOrdersEvents] = useState<Event[]>([])
  const [siteVisitsEvents, setSiteVisitsEvents] = useState<Event[]>([])

  const [territoryOptions, setTerritoryOptions] =
    useState<Options[]>(preTerritoryOptions)

  const [territoryParam, setTerritoryParam] = useState<string>(
    getTerritoryParam(territoryOptions)
  )

  const [territoryManagersOptions, setTerritoryManagersOptions] =
    useState<Options[]>([])

  const [affiliatesOptions, setAffiliatesOptions] =
    useState<Options[]>(preAffiliateOptions)

  const [affiliateParam, setAffiliateParam] = useState<string>(
    getTerritoryParam(affiliatesOptions))

  const [vendorOptions, setVendorOptions] = useState<VendorOptionProp[]>([])
  const [vendorOptionsChecked, setVendorOptionsChecked] = useState<VendorOptionProp[]>([])

  const [showOrders, setShowOrders] = useState(true)
  const [showSiteVisits, setShowSiteVisits] = useState(true)

  const [showAssignedToMe, setShowAssignedToMe] = useState(false)

  const [loadingOrders, setLoadingOrders] = useState<boolean>(true)
  const [loadingVendors, setLoadingVendors] = useState<boolean>(true)

  const loggedUserEmail = useSelector(getEmployeePrimaryEmail)
  const vendorUserIds = vendors.filter((vendor) =>
    vendor?.email[0]?.email?.includes(loggedUserEmail?.split('@')[0])
  ).reduce((acc: any, curr: any) => (acc = [...acc, curr.id]), []);


  const reduceOrdersForEvents = (orders: OrderType[], type: 'ORDER' | 'SITE_VISIT' | 'WARRANTY' | 'RECALL' | 'MAINTENANCE'): Event[] => {
    // Filter orders to exclude those from unapproved vendors, non-bosscathome.com emails and unchecked vendors
    const approvedVendorIds = vendors
      .filter(vendor =>
        vendor.approved &&
        vendor.email?.some(email => email.email?.includes('bosscathome.com'))
      )
      .map(vendor => vendor.id);

    const checkedVendorIds = vendorOptionsChecked.map(vendor => vendor.id);

    const filteredOrders = orders.filter(order =>
      approvedVendorIds.includes(order.vendorId) &&
      checkedVendorIds.includes(order.vendorId)
    );

    const ordersReduced = filteredOrders.reduce((prev: Event[], order: OrderType, index: number) => {
      if (type !== order.type) {
        return prev;
      }
      const result = prev
      const startDate = moment(formatTimestampDate(order.scheduledStartTimestamp ?? 0)).toDate()
      const startPlus1Hour = moment(startDate).add(1, 'hours').toDate()
      const backgroundColor = getOrderStatusColor(order?.statusType ?? '')
      const newEvent = {
        title: ViewEvent(index, order, vendors, vendorOptions, view, backgroundColor, type),
        start: formatTimestampDate(order.scheduledStartTimestamp ?? 0),
        end: order.scheduledEndTimestamp ? formatTimestampDate(order.scheduledEndTimestamp) : startPlus1Hour,
        resource: !order.preapproval ? `jobs/${order.estimateId}?tab=1&order=${order?.orderId}` : `estimates/${order.estimateId}?tab=1`,
      }
      if (newEvent.title !== null) result.push(newEvent)
      return result
    }, [])
    return ordersReduced
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (vendors.length > 0) {
      if (showOrders && orders.length) {
        const orderEvents = reduceOrdersForEvents(orders, 'ORDER')
        const warrantyEvents = reduceOrdersForEvents(orders, 'WARRANTY')
        const recallEvents = reduceOrdersForEvents(orders, 'RECALL')
        const maintenanceEvents = reduceOrdersForEvents(orders, 'MAINTENANCE')
        setOrdersEvents([...orderEvents, ...warrantyEvents, ...recallEvents, ...maintenanceEvents])
      }

      if (showSiteVisits && siteVisits.length) {
        setSiteVisitsEvents(reduceOrdersForEvents(siteVisits, 'SITE_VISIT'))
      }
    }
  }, [orders, siteVisits, vendors, showSiteVisits, showOrders, vendorOptionsChecked])

  useEffect(() => {
    if (vendorOptions.length > 0) {
      setVendorOptionsChecked(vendorOptions.filter((vendor) => vendor.checked))
    }
  }, [vendorOptions])

  useEffect(() => {
    setTerritoryOptions([
      ...preTerritoryOptions,
      ...territories
        .filter(({ serviceable }) => serviceable === true)
        .map(({ id, title }) => ({
          id,
          label: title,
          hide: false,
        })),
    ])
  }, [territories])

  useEffect(() => {

    setAffiliatesOptions([
      ...preAffiliateOptions,
      ...affiliatesStages
        .map(({ id, name }) => ({
          id,
          label: name,
          hide: false,
        })),
    ])

  }, [affiliatesStages, setAffiliatesOptions])

  // needs attention
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const vendorsFormatted = vendors.map((vendor) => ({
      id: vendor.id,
      checked: vendorOptionsChecked.length ? false : vendorUserIds.includes(vendor.id) ? true : !showAssignedToMe
    }))
    const vendorsWithoutChecked = vendorsFormatted.filter((vendor) => !vendorOptionsChecked.includes(vendor))
    const vendorsWithChecked = [...vendorOptionsChecked, ...vendorsWithoutChecked]

    setVendorOptions(
      vendorsWithChecked
    )
    // update vendor colors
    const nullColorVendor = vendors.findIndex((vendor) => vendor.color === null) // vendor index with null color

    if (nullColorVendor > -1) {
      dispatch(
        vendorsActions.setVendorsList(
          vendors.map((vendor, index) => ({
            ...vendor,
            color: vendor.color ? vendor.color : rainbow(index),
          }))
        )
      )
    }
  }, [vendors, dispatch, showAssignedToMe])

  useEffect(() => {
    setLoadingOrders(true)
    setLoadingVendors(true)
    setVendorOptions([])
    setSiteVisitsEvents([])
    setOrdersEvents([])

    let ordersParams = {
      startDate: selected!.clone().startOf(view).format('MM/DD/YYYY'),
      endDate: selected!.clone().endOf(view).add(1, 'days').format('MM/DD/YYYY'),
      limit: 1000,
      statuses: [
        ORDER_STATUS.DISPATCHED,
        ORDER_STATUS.REVIEW,
        ORDER_STATUS.COMPLETE,
        ORDER_STATUS.IN_PROGRESS,
        ORDER_STATUS.PENDING,
      ].join(','),
      territory: territoryParam,
      affiliate: affiliateParam,
    } as any

    if (territoryManagersOptions.length > 0) {
      ordersParams.territoryManager = territoryManagersOptions[0].id
    }

    showOrders && dispatch(
      ordersActions.fetchOrders(
        {
          params: ordersParams
        },
        (_succ) => {
          setLoadingOrders(false)
        }
      )
    )

    let siteVisitsParams = {
      startDate: selected!.clone().startOf(view).format('MM/DD/YYYY'),
      endDate: selected!.clone().endOf(view).add(1, 'days').format('MM/DD/YYYY'),
      limit: 1000,
      status: [
        ORDER_STATUS.DISPATCHED,
        ORDER_STATUS.REVIEW,
        ORDER_STATUS.COMPLETE,
        ORDER_STATUS.IN_PROGRESS,
        ORDER_STATUS.PENDING
      ].join(','),
      territory: territoryParam,
      affiliate: affiliateParam,
    } as any

    if (territoryManagersOptions.length > 0) {
      siteVisitsParams.territoryManager = territoryManagersOptions[0].id
    }

    showSiteVisits && dispatch(
      siteVisitsActions.getSiteVisits(siteVisitsParams, undefined, undefined,
        (_succ) => {
          setLoadingOrders(false)
        })
    )

    dispatch(
      vendorsActions.fetchVendors({
        page: 0,
        size: 1000,
        searchParams: {
          approved: true,
          territory: territoryParam,
        },
      }, (_succ) => {
        setLoadingVendors(false)
      })
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, view, dispatch, territoryParam, affiliateParam, showOrders, showSiteVisits, territoryManagersOptions])

  useEffect(() => {
    dispatch(territoriesActions.fetchTerritories((succ) => { }))
    dispatch(companiesActions.fetchCompanies())
    dispatch(
      territoryActions.fetchTerritoryManagers(
        `${EMPLOYEE_ROLE_TYPE.TERRITORY_MANAGER}`,
        (_succ: boolean) => {
          // setPageLoading(false)
        }
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CalendarContext.Provider
      value={{
        ordersEvents,
        siteVisitsEvents,
        selected,
        setSelected,
        view,
        setView,
        showDispatch,
        setShowDispatch,
        territoryOptions,
        setTerritoryOptions,
        territoryParam,
        setTerritoryParam,
        affiliatesOptions,
        setAffiliatesOptions,
        affiliateParam,
        setAffiliateParam,
        vendorOptions,
        setVendorOptions,
        showOrders,
        setShowOrders,
        showSiteVisits,
        setShowSiteVisits,
        showAssignedToMe,
        setShowAssignedToMe,
        territoryManagersOptions,
        setTerritoryManagersOptions
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridAutoFlow: 'row',
          gridAutoRows: 'min-content min-content auto',
        }}
      >
        <CalendarToolbar />
        <Divider sx={{ margin: '16px -24px' }} />
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'auto min-content',
            gap: '20px',
          }}
        >
          <PrivateLoader loading={loadingOrders || loadingVendors}>
            {showDispatch ? <ViewScheduler /> : <ViewCalendar />}
          </PrivateLoader>
          <CalendarSidebar />
        </Box>
      </Box>
    </CalendarContext.Provider>
  )
}

export default CalendarPage
